import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, ArrowUp } from 'lucide-react';
import PlatformCards from '../components/PlatformCards';
import LocationSelector from '../components/LocationSelector';
import { VideoBackground } from '../components/VideoBackground';
import HomePage from '../components/HomePage';
import OnboardingDetail from '../components/OnboardingDetail';
import FeedbackButton from '../components/FeedbackButton';
import Disclaimer from '../components/Disclaimer';
import AIAssistant from '../components/AIAssistant';
import PlatformDetailsView from '../components/PlatformDetailsView';
import { DeviceDetailModal } from '../components/modals/DeviceDetailModal';
import { PartnerProductModal } from '../components/modals/PartnerProductModal';
import { ProductDetailModal } from '../components/modals/ProductDetailModal';
import { OnboardingModal } from '../components/modals/OnboardingModal';
import { useProductSearch } from '../hooks/useProductSearch';
import { useTabGeneration } from '../hooks/useTabGeneration';
import { useProductProcessing } from '../hooks/useProductProcessing';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { usePlatformData } from '../hooks/usePlatformData';
import { useOnboardingData } from '../hooks/useOnboardingData';
import { useRouteSync } from '../hooks/useRouteSync';
import { useScrollBehavior } from '../hooks/useScrollBehavior';
import { cn } from '../lib/utils';

export type Product = {
  id: number;
  platform: string;
  platform_description?: string;
  solution: string;
  solution_description?: string;
  development: string;
  hardware_family: string;
  model: string;
  patt: string;
  category: string;
  tag: string;
  vertical: string;
  solution_level: number | string;
  countries: Record<string, boolean | string>;
  regions: string[];
  notes: string;
};

export type OnboardingSystem = {
  id: number;
  platform: string;
  country: string;
  gma: string;
  manual: string;
  sfeu_insights: string;
  cognito: string;
  sales_center: string;
  sf_usa: string;
  checkmate: string;
  manual_eu: string;
  s4eu_alloy: string;
  mp_lbm: string;
  ltm: string;
  mp_insights: string;
  partner_portal: string;
  pp_insights: string;
  cm_lbm: string;
  tsys: string;
  monthly_statements: string;
  lt_lbm: string;
  lt_insights: string;
};

export type ColumnGroup = { key: string; label: string };

export type ColumnGroups = {
  boardingSystems: ColumnGroup[];
  underwritingSystems: ColumnGroup[];
  merchantPortal: ColumnGroup[];
  partnerPortal: ColumnGroup[];
  chargebackManagement: ColumnGroup[];
  monthlyStatements: ColumnGroup[];
  liveTransactions: ColumnGroup[];
};

export type SearchFiltersType = {
  country?: string;
  region?: string;
  platform?: string;
  vertical?: string;
  development?: string;
  search?: string;
};

export type PlatformInfo = {
  name: string;
  description: string;
  isAvailable: boolean;
  productCount: number;
  products: Product[];
};

export const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'Canada', name: 'Canada', flag: '🇨🇦' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'Ireland', name: 'Ireland', flag: '🇮🇪' },
  { code: 'Germany', name: 'Germany', flag: '🇩🇪' },
  { code: 'France', name: 'France', flag: '🇫🇷' },
  { code: 'Spain', name: 'Spain', flag: '🇪🇸' },
  { code: 'Italy', name: 'Italy', flag: '🇮🇹' },
  { code: 'Netherlands', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'Belgium', name: 'Belgium', flag: '🇧🇪' },
  { code: 'Austria', name: 'Austria', flag: '🇦🇹' },
  { code: 'Switzerland', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'Sweden', name: 'Sweden', flag: '🇸🇪' },
  { code: 'Norway', name: 'Norway', flag: '🇳🇴' },
  { code: 'Denmark', name: 'Denmark', flag: '🇩🇰' },
  { code: 'Finland', name: 'Finland', flag: '🇫🇮' },
  { code: 'Poland', name: 'Poland', flag: '🇵🇱' },
  { code: 'Czechia', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'Hungary', name: 'Hungary', flag: '🇭🇺' },
  { code: 'Lithuania', name: 'Lithuania', flag: '🇱🇹' },
  { code: 'Australia', name: 'Australia', flag: '🇦🇺' },
  { code: 'New Zealand', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'Brazil', name: 'Brazil', flag: '🇧🇷' },
  { code: 'Israel', name: 'Israel', flag: '🇮🇱' },
];

export function getCountryFlag(countryName: string): string {
  const c = countryName.toLowerCase();
  if (c.includes('united states') || c.includes('usa') || c === 'us') return '🇺🇸';
  if (c.includes('canada')) return '🇨🇦';
  if (c.includes('united kingdom') || c.includes('uk')) return '🇬🇧';
  if (c.includes('germany')) return '🇩🇪';
  if (c.includes('france')) return '🇫🇷';
  if (c.includes('italy')) return '🇮🇹';
  if (c.includes('spain')) return '🇪🇸';
  if (c.includes('netherlands')) return '🇳🇱';
  if (c.includes('belgium')) return '🇧🇪';
  if (c.includes('poland')) return '🇵🇱';
  if (c.includes('ireland')) return '🇮🇪';
  if (c.includes('austria')) return '🇦🇹';
  if (c.includes('australia')) return '🇦🇺';
  if (c.includes('new zealand')) return '🇳🇿';
  return '🌍';
}

const lastUpdated = '2026-10-09';

function CatalogPage() {
  const { platformName, region, country, countryCode } = useParams<{
    platformName?: string;
    region?: string;
    country?: string;
    countryCode?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useLocation(); // consumed by useRouteSync internally
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { products, loading, error, searchProducts } = useProductSearch();

  const {
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    selectedPlatform,
    setSelectedPlatform,
    currentView,
    setCurrentView,
    browseMode,
    setBrowseMode,
    platforms,
    setPlatforms,
    platformProducts,
    lbm2Products,
    onboardingSystems,
    onboardingColumnGroups,
    activeTab,
    setActiveTab,
    tabSearchQuery,
    showScrollToTop,
    isAIDrawerOpen,
    setIsAIDrawerOpen,
    aiDrawerWidth,
    setSearchQuery,
    setSearchResults,
    setIsSearching,
    setShowSearchDropdown,
    options,
    setOptions,
    selectedDevice,
    showDeviceModal,
    handleCloseDeviceModal,
    selectedPartnerProduct,
    showPartnerModal,
    handleClosePartnerModal,
    selectedProduct,
    showProductModal,
    handleCloseProductModal,
    selectedOnboardingCountry,
    showOnboardingModal,
    handleCloseOnboardingModal,
  } = useProductMatrix();

  // ── Hooks ──────────────────────────────────────────────────────────────────
  useRouteSync({ platformName, region, country, countryCode, searchParams, searchProducts });
  usePlatformData({ selectedPlatform, currentView, browseMode, selectedCountry, selectedRegion });
  useOnboardingData({ selectedPlatform, currentView });
  useScrollBehavior({ scrollContainerRef, searchParams });

  // ── Filter options (loaded once) ───────────────────────────────────────────
  useEffect(() => {
    fetch('/api/filters')
      .then(r => r.json())
      .then(data => { if (data.success) setOptions(data.data); })
      .catch(err => console.error('Failed to load filter options:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load products when location filters change ─────────────────────────────
  useEffect(() => {
    if (selectedRegion || selectedCountry || browseMode) {
      const filters: SearchFiltersType = {};
      if (selectedCountry) filters.country = selectedCountry;
      if (selectedRegion) filters.region = selectedRegion;
      searchProducts(filters);
    }
  }, [selectedRegion, selectedCountry, browseMode, searchProducts]);

  // ── Build platform cards from product list ─────────────────────────────────
  useEffect(() => {
    if (products.length === 0) { setPlatforms([]); return; }

    const platformMap = new Map<string, PlatformInfo>();
    products
      .filter(p => String(p.solution_level) !== '2')
      .forEach(product => {
        if (!platformMap.has(product.platform)) {
          platformMap.set(product.platform, {
            name: product.platform,
            description: product.platform_description || '',
            isAvailable: false,
            productCount: 0,
            products: [],
          });
        }
        const platform = platformMap.get(product.platform)!;
        const available = browseMode
          ? true
          : selectedCountry
            ? product.countries[selectedCountry]
            : selectedRegion
              ? product.regions.includes(selectedRegion)
              : false;
        if (available) platform.isAvailable = true;
        platform.products.push(product);
        platform.productCount++;
      });

    const result = Array.from(platformMap.values());

    // Static placeholders for platforms not yet in data source
    const STATIC_PLATFORMS = [
      { name: 'Tax Free Shopping', description: '' },
      { name: 'Legacy', description: '' },
    ];
    for (const { name, description } of STATIC_PLATFORMS) {
      if (!platformMap.has(name)) {
        result.push({ name, description, isAvailable: false, productCount: 0, products: [] });
      }
    }

    setPlatforms(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedRegion, selectedCountry, browseMode]);

  // ── Two-pass processing & tab generation ──────────────────────────────────
  const productProcessing1 = useProductProcessing({
    platformProducts, selectedPlatform, tabSearchQuery, products,
    tabs: undefined, COUNTRIES, selectedCountry, currentView, browseMode,
  });

  const tabs = useTabGeneration({
    selectedPlatform, platformProducts, products,
    hardwareProducts: productProcessing1.hardwareProducts,
    softwareProducts: productProcessing1.softwareProducts,
    unifiedVisibleCountries: productProcessing1.unifiedVisibleCountries,
    level2Platforms: productProcessing1.level2Platforms,
    lbm2Products, onboardingSystems, COUNTRIES, browseMode, selectedCountry,
  });

  const { unifiedVisibleCountries, showMainLanding } = useProductProcessing({
    platformProducts, selectedPlatform, tabSearchQuery, products,
    tabs, COUNTRIES, selectedCountry, currentView, browseMode,
  });

  // ── Sync active tab with URL param ─────────────────────────────────────────
  useEffect(() => {
    if (tabs.length === 0 || !selectedPlatform) return;
    const tabParam = searchParams.get('tab');
    if (tabParam && tabs.find(t => t.id === tabParam)) {
      if (activeTab !== tabParam) setActiveTab(tabParam);
    } else if (!activeTab || !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length, selectedPlatform, searchParams, activeTab]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCountryClick = (code: string) => navigate(`/onboarding/${code}`);

  const handleBackFromOnboarding = () => navigate('/onboarding');

  const handlePlatformClick = (platform: PlatformInfo) => {
    if (!platform.isAvailable) return;
    const encoded = encodeURIComponent(platform.name);
    const params = new URLSearchParams(searchParams);
    navigate(params.toString() ? `/platform/${encoded}?${params}` : `/platform/${encoded}`);
  };

  const handleResetToInitial = () => {
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedPlatform(null);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setShowSearchDropdown(false);
    setActiveTab('');
    navigate('/');
  };

  const scrollToTop = () =>
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  // ── View resolution (Strategy pattern) ────────────────────────────────────
  type ViewKey = 'home' | 'location' | 'onboarding' | 'platform' | 'platform-selection';
  const resolveViewKey = (): ViewKey => {
    if (showMainLanding) return 'home';
    if (currentView === 'location') return 'location';
    if (currentView === 'onboarding' && selectedOnboardingCountry) return 'onboarding';
    if (currentView === 'platform') return 'platform';
    return 'platform-selection';
  };
  const viewKey = resolveViewKey();
  const isHomeView = viewKey === 'home';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className="h-screen relative text-foreground overflow-hidden transition-all duration-300"
        style={{ marginRight: isAIDrawerOpen && window.innerWidth >= 1024 ? `${aiDrawerWidth}px` : '0' }}
      >
        <div className="absolute inset-0">
          <VideoBackground
            icon={isHomeView ? Globe : undefined}
            title={isHomeView ? 'Product Catalog' : undefined}
            description={isHomeView
              ? "Quickly see what's available in each region, the supported hardware, payment methods, integrations, and partner integration options — all in one place."
              : undefined}
            showOverlay={false}
            theme="light"
          />
        </div>

        <Disclaimer theme="light" isAIDrawerOpen={isAIDrawerOpen} aiPanelWidth={aiDrawerWidth} />

        {viewKey === 'home' && (
          <div className="pt-14">
            <HomePage
              platforms={platforms}
              onCountryClick={handleCountryClick}
              loading={loading}
              theme="light"
            />
          </div>
        )}

        {viewKey === 'location' && (
          <div className="pt-14">
            <LocationSelector options={options} />
          </div>
        )}

        {viewKey === 'onboarding' && selectedOnboardingCountry && (
          <div className="pt-14">
            <OnboardingDetail
              countryCode={selectedOnboardingCountry.country}
              countryName={selectedOnboardingCountry.countryName}
              onBack={handleBackFromOnboarding}
              theme="light"
            />
          </div>
        )}

        {(viewKey === 'platform-selection' || viewKey === 'platform') && (
          <div className={cn(
            'absolute flex items-center justify-center',
            viewKey === 'platform' ? 'top-14 bottom-0 left-0 right-0 p-0' : 'inset-0 pt-28 lg:pt-20 p-4 lg:p-8',
          )}>
            <div className={cn(
              'w-full flex flex-col overflow-hidden relative',
              viewKey === 'platform' ? 'max-w-none bg-card h-full lg:bg-card lg:rounded-none lg:shadow-none lg:border-0' : 'max-w-6xl h-full lg:h-[80vh]',
            )}>
              {viewKey === 'platform' && (
                <>
                  <div className="absolute top-0 right-0 w-72 h-72 opacity-0 pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #9CA3AF 2px, transparent 2px)', backgroundSize: '24px 24px' }} />
                  <div className="absolute top-[-75px] right-48 w-40 h-40 rounded-full opacity-0 pointer-events-none"
                    style={{ background: 'linear-gradient(135deg, #1d4ed8, #1d4ed8)', filter: 'blur(0.5px)' }} />
                  <div className="absolute top-1/2 left-[-150px] w-64 h-64 rounded-full opacity-0 pointer-events-none"
                    style={{ border: '40px solid #6BD8FF', background: 'transparent' }} />
                </>
              )}

              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-thin">
                <div className="p-6 lg:p-8">
                  {viewKey === 'platform' && selectedPlatform && (
                    <PlatformDetailsView COUNTRIES={COUNTRIES} getCountryFlag={getCountryFlag} lastUpdated={lastUpdated} />
                  )}

                  {viewKey === 'platform-selection' && (
                    <PlatformSelectionView
                      browseMode={browseMode}
                      selectedCountry={selectedCountry}
                      loading={loading}
                      error={error}
                      platforms={platforms}
                      onBack={() => {
                        if (selectedCountry) {
                          setSelectedCountry('');
                          setBrowseMode(false);
                          setCurrentView('location');
                          navigate('/location');
                        } else {
                          setBrowseMode(false);
                          setCurrentView('selection');
                          navigate('/');
                        }
                      }}
                      onPlatformClick={handlePlatformClick}
                      onReset={handleResetToInitial}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeviceModal && selectedDevice && (
          <DeviceDetailModal device={selectedDevice} onClose={handleCloseDeviceModal} visibleCountries={unifiedVisibleCountries} />
        )}
        {showPartnerModal && selectedPartnerProduct && (
          <PartnerProductModal product={selectedPartnerProduct} onClose={handleClosePartnerModal} visibleCountries={unifiedVisibleCountries} />
        )}
        {showProductModal && selectedProduct && (
          <ProductDetailModal product={selectedProduct} onClose={handleCloseProductModal} visibleCountries={unifiedVisibleCountries} />
        )}
        {showOnboardingModal && selectedOnboardingCountry && onboardingColumnGroups && (
          <OnboardingModal country={selectedOnboardingCountry} columnGroups={onboardingColumnGroups} onClose={handleCloseOnboardingModal} />
        )}

        <FeedbackButton feedbackEmail="kacper.lenczuk@shift4.com" isAIDrawerOpen={isAIDrawerOpen} />

        {showScrollToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-20 right-6 md:bottom-20 md:right-6 w-12 h-12 md:w-14 md:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out flex items-center justify-center z-40 group"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform duration-200" />
          </button>
        )}
      </div>

      <AIAssistant isOpen={isAIDrawerOpen} onClose={() => setIsAIDrawerOpen(false)} />
    </>
  );
};

// ── PlatformSelectionView ──────────────────────────────────────────────────
type PlatformSelectionViewProps = {
  browseMode: boolean;
  selectedCountry: string;
  loading: boolean;
  error: string | null;
  platforms: PlatformInfo[];
  onBack: () => void;
  onPlatformClick: (p: PlatformInfo) => void;
  onReset: () => void;
};

function PlatformSelectionView({
  browseMode, selectedCountry, loading, error, platforms, onBack, onPlatformClick, onReset,
}: PlatformSelectionViewProps) {
  return (
  <>
    {browseMode && (
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
          {selectedCountry ? 'Back to location selector' : 'Back to home'}
        </button>
      </div>
    )}

    {loading && (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="relative">
          <div className="rounded-full h-10 w-10 border-4 border-muted border-t-primary" />
          <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-pulse" />
        </div>
        <div className="mt-4 text-center">
          <p className="text-base font-medium text-foreground animate-pulse">Loading solutions...</p>
          <p className="text-sm text-muted-foreground mt-1">Discovering available products</p>
        </div>
      </div>
    )}

    {error && (
      <div className="animate-slide-in-from-top">
        <div className="glass-card border-destructive/20 p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
            <div className="text-destructive font-medium text-sm">Error loading solutions</div>
          </div>
          <p className="text-destructive/80 mt-2 text-sm">{error}</p>
        </div>
      </div>
    )}

    {!loading && !error && platforms.length > 0 && (
      <div>
        <div className="mb-8 mt-8 text-left">
          <p className="text-lg text-white/90 font-medium">Select a product platform to explore</p>
        </div>
        <div className="animate-slide-in-from-bottom">
          <PlatformCards platforms={platforms} onPlatformClick={onPlatformClick} />
        </div>
      </div>
    )}

    {!loading && !error && platforms.length === 0 && (
      <div className="text-center py-12">
        <div className="glass-card p-6">
          <div className="text-4xl mb-4 opacity-50">🔍</div>
          <h3 className="heading-sm mb-2 text-muted-foreground">No solutions found</h3>
          <p className="body-sm mb-6">
            No products are currently available in the selected {selectedCountry ? 'country' : 'region'}.
          </p>
          <button
            onClick={onReset}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10" />
            </svg>
            Start Over
          </button>
        </div>
      </div>
    )}
  </>
  );
}

export default CatalogPage;
